import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AIEmailWriter, AISidebar } from './AIPanel';
import AIEstimateGenerator from './AIEstimateGenerator';
import ExpansionPanel from './ExpansionPanel';
import ProposalTiers from './ProposalTiers';
import { useIsMobile } from '../../hooks/useIsMobile';

const API = process.env.REACT_APP_BACKEND_URL;
const STAGES = [
  { key: 'new', label: 'New', color: '#3b82f6' },
  { key: 'contact_attempted', label: 'Contact Attempted', color: '#6366f1' },
  { key: 'contacted', label: 'Contacted', color: '#8b5cf6' },
  { key: 'qualified', label: 'Qualified', color: '#f59e0b' },
  { key: 'site_visit', label: 'Site Visit', color: '#f97316' },
  { key: 'estimate_in_progress', label: 'Estimate In Progress', color: '#ef4444' },
  { key: 'estimate_sent', label: 'Estimate Sent', color: '#ec4899' },
  { key: 'follow_up', label: 'Follow Up', color: '#a855f7' },
  { key: 'customer_decision', label: 'Customer Decision', color: '#14b8a6' },
  { key: 'deposit_requested', label: 'Deposit Requested', color: '#22c55e' },
  { key: 'won', label: 'Won', color: '#16a34a' },
  { key: 'lost', label: 'Lost', color: '#6b7280' },
  { key: 'disqualified', label: 'DQ', color: '#9ca3af' },
];
const LOST_REASONS = ['competitor','price','financing','project_cancelled','project_postponed','no_response','diy','outside_service_area','scope_changed','other'];
const ACTIVITY_TYPES = ['call','email','note','text','meeting','site_visit','estimate_sent','financing_sent'];

import { authFetch } from '../../utils/salesAuth';
import { fmtDateTime, fmtDate } from '../../utils/dateUtils';
const h = () => ({ 'Content-Type': 'application/json' });
const authOpts = (opts = {}) => ({ ...opts, credentials: 'include', headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) } });
function money(v) { return v ? '$' + Math.round(v).toLocaleString() : '$0'; }
function fmt(d) { return fmtDateTime(d); }

export default function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('timeline');
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [showLostModal, setShowLostModal] = useState(false);
  const [showEditOpp, setShowEditOpp] = useState(false);
  const [files, setFiles] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [aiDraft, setAiDraft] = useState(null);
  const [showDetails, setShowDetails] = useState(!isMobile);
  const [extractions, setExtractions] = useState([]);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/api/sales/opportunities/${id}`, authOpts());
    if (res.status === 401) { navigate('/sales/login'); return; }
    const d = await res.json();
    setData(d);
    const fRes = await fetch(`${API}/api/files/by-opportunity/${id}`, authOpts());
    if (fRes.ok) setFiles(await fRes.json());
    const eRes = await fetch(`${API}/api/sales/email/history/${id}`, authOpts());
    if (eRes.ok) setEmailHistory(await eRes.json());
    const xRes = await fetch(`${API}/api/sales/opportunity/${id}/extractions`, authOpts());
    if (xRes.ok) setExtractions(await xRes.json());
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  const { opportunity: opp, customer, activities, tasks, estimates } = data;
  const currentStage = STAGES.find(s => s.key === opp.current_stage) || { label: opp.current_stage, color: '#6b7280' };

  const changeStage = async (stage, lostReason, lostDetail) => {
    await fetch(`${API}/api/sales/opportunities/${id}/stage`, authOpts({
      method: 'PUT', body: JSON.stringify({ stage, lost_reason: lostReason, lost_reason_detail: lostDetail }),
    }));
    setShowStageModal(false);
    setShowLostModal(false);
    load();
  };

  return (
    <div data-testid="opportunity-detail" style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif', maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: isMobile ? '10px 12px' : '14px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 14, flex: 1, minWidth: 0 }}>
          <button data-testid="back-btn" onClick={() => navigate('/sales')} style={{ padding: '6px 10px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13, flexShrink: 0, minHeight: 36 }}>Back</button>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h1 style={{ fontSize: isMobile ? 15 : 18, fontWeight: 700, color: '#1e293b', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opp.title}</h1>
            <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {customer.name}
              {customer.phone && !isMobile ? ` • ${customer.phone}` : ''}
              {customer.email && !isMobile ? ` • ${customer.email}` : ''}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <span style={{ background: `${currentStage.color}15`, color: currentStage.color, padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>{currentStage.label}</span>
          <span style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: '#1e293b' }}>{money(opp.quoted_value || opp.estimated_value)}</span>
        </div>
      </div>

      {/* Mobile Quick Actions Bar */}
      {isMobile && (
        <div data-testid="mobile-quick-actions" style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '8px 12px', display: 'flex', gap: 6, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {customer.phone && (
            <a href={`tel:${customer.phone}`} data-testid="quick-call" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', background: '#16a34a', color: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap', minHeight: 40 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
              Call
            </a>
          )}
          {customer.email && (
            <a href={`mailto:${customer.email}`} data-testid="quick-email-link" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', background: '#2563eb', color: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap', minHeight: 40 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="M22 6l-10 7L2 6"/></svg>
              Email
            </a>
          )}
          <button onClick={() => setShowActivityForm(true)} data-testid="quick-add-note" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', background: '#f59e0b', color: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 40 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            Note
          </button>
          <button onClick={() => setShowTaskForm(true)} data-testid="quick-followup" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', background: '#8b5cf6', color: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 40 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
            Follow-Up
          </button>
          <button onClick={() => { setShowActivityForm(true); }} data-testid="quick-site-visit" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', background: '#f97316', color: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 40 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            Site Visit
          </button>
          <button onClick={() => setTab('estimates')} data-testid="quick-estimate" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', background: '#c0392b', color: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 40 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 7h6m-6 4h6m-6 4h4m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
            Estimate
          </button>
        </div>
      )}

      {/* Mobile Customer Contact Info */}
      {isMobile && (
        <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '8px 12px' }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: '#64748b' }}>
            {customer.phone && <a href={`tel:${customer.phone}`} style={{ color: '#c0392b', fontWeight: 500 }}>{customer.phone}</a>}
            {customer.email && <a href={`mailto:${customer.email}`} style={{ color: '#2563eb', fontWeight: 500 }}>{customer.email}</a>}
            {customer.address && <span>{customer.address}, {customer.city}</span>}
          </div>
        </div>
      )}

      {/* Stage Progression Bar */}
      <div style={{ background: '#fff', padding: isMobile ? '8px 0 8px 12px' : '12px 28px', borderBottom: '1px solid #e2e8f0', maxWidth: '100%', boxSizing: 'border-box' }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingRight: isMobile ? 12 : 0, paddingBottom: 4 }}>
          <div style={{ display: 'flex', gap: 2, minWidth: isMobile ? 580 : 900 }}>
            {STAGES.filter(s => !['lost','disqualified'].includes(s.key)).map((s, i) => {
              const active = s.key === opp.current_stage;
              const idx = STAGES.findIndex(st => st.key === opp.current_stage);
              const stIdx = STAGES.findIndex(st => st.key === s.key);
              const passed = stIdx < idx;
              return (
                <button key={s.key} onClick={() => {
                  if (s.key === 'lost' || s.key === 'disqualified') return;
                  if (!active) setShowStageModal(s.key);
                }} style={{
                  flex: 1, padding: '6px 4px', border: 'none', borderRadius: 4, fontSize: isMobile ? 9 : 10, fontWeight: 600, cursor: 'pointer', textAlign: 'center',
                  background: active ? s.color : passed ? `${s.color}30` : '#f1f5f9',
                  color: active ? '#fff' : passed ? s.color : '#94a3b8', minHeight: 32, whiteSpace: 'nowrap',
                }}>{s.label}</button>
              );
            })}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8, paddingRight: isMobile ? 12 : 0 }}>
          <button onClick={() => setShowLostModal('lost')} style={{ padding: '4px 10px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 4, fontSize: 11, cursor: 'pointer', minHeight: 36 }}>Mark Lost</button>
          <button onClick={() => setShowLostModal('disqualified')} style={{ padding: '4px 10px', background: '#f8fafc', color: '#6b7280', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 11, cursor: 'pointer', minHeight: 36 }}>Disqualify</button>
        </div>
      </div>

      {showStageModal && <ConfirmModal title={`Move to ${STAGES.find(s=>s.key===showStageModal)?.label}?`} onConfirm={() => changeStage(showStageModal)} onCancel={() => setShowStageModal(false)} />}
      {showLostModal && <LostReasonModal type={showLostModal} onSubmit={(r,d) => changeStage(showLostModal, r, d)} onCancel={() => setShowLostModal(false)} />}

      {/* Content */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 340px', gap: 0, maxWidth: '100%', overflow: 'hidden' }}>
        {/* Left - Main Content */}
        <div style={{ padding: isMobile ? '12px 12px' : '20px 28px', minWidth: 0, overflow: 'hidden' }}>
          {/* Action Buttons (Desktop) */}
          {!isMobile && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <ActionBtn label="Log Activity" onClick={() => setShowActivityForm(true)} color="#3b82f6" />
              <ActionBtn label="Send Email" onClick={() => setShowEmailComposer(true)} color="#c0392b" />
              <ActionBtn label="Add Task" onClick={() => setShowTaskForm(true)} color="#f59e0b" />
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 2, marginBottom: 16, background: '#e2e8f0', borderRadius: 8, padding: 3, overflowX: isMobile ? 'auto' : 'visible', WebkitOverflowScrolling: 'touch' }}>
            {['timeline', 'tasks', 'files', 'emails', 'estimates'].map(t => (
              <button key={t} onClick={() => setTab(t)} style={{
                flex: isMobile ? '0 0 auto' : 1, padding: isMobile ? '7px 14px' : '7px 0', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: tab === t ? '#fff' : 'transparent', color: tab === t ? '#1e293b' : '#64748b', whiteSpace: 'nowrap', minHeight: 36,
              }}>{t.charAt(0).toUpperCase() + t.slice(1)}{t === 'tasks' ? ` (${tasks.length})` : t === 'files' ? ` (${files.length})` : ''}</button>
            ))}
          </div>

          {/* Tab Content */}
          {tab === 'timeline' && <TimelineTab activities={activities} />}
          {tab === 'tasks' && <TasksTab tasks={tasks} onRefresh={load} />}
          {tab === 'files' && <FilesTab files={files} customerId={customer.id} oppId={id} onRefresh={load} />}
          {tab === 'emails' && <EmailsTab emails={emailHistory} />}
          {tab === 'estimates' && (
            <>
              <EstimateAttacher oppId={id} customerId={customer.id} onDone={load} extractions={extractions} />
              <AIEstimateGenerator oppId={id} onEstimateSaved={load} />
              <EstimatesTab estimates={estimates} />
            </>
          )}
        </div>

        {/* Right Sidebar - Customer + Opp Details */}
        {isMobile ? (
          <div style={{ borderTop: '1px solid #e2e8f0', background: '#fff', padding: '12px 12px', minWidth: 0, overflow: 'hidden' }}>
            <button onClick={() => setShowDetails(!showDetails)} data-testid="toggle-details"
              style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
              Customer & Opportunity Details
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: showDetails ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}><path d="M6 9l6 6 6-6"/></svg>
            </button>
            {showDetails && (
              <div style={{ paddingTop: 8 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Customer</h3>
                <DetailRow label="Name" value={customer.name} />
                <DetailRow label="Phone" value={customer.phone} isPhone />
                <DetailRow label="Email" value={customer.email} isEmail />
                <DetailRow label="Address" value={[customer.address, customer.city, customer.state, customer.zip_code].filter(Boolean).join(', ')} />
                <DetailRow label="Company" value={customer.company} />
                <DetailRow label="Source" value={(opp.lead_source || '').replace(/_/g, ' ')} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0 12px' }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: 0 }}>Opportunity</h3>
                  <button data-testid="edit-opportunity-btn" onClick={() => setShowEditOpp(!showEditOpp)} style={{
                    padding: '5px 12px', fontSize: 11, fontWeight: 600, border: '1px solid #c0392b', borderRadius: 4,
                    background: showEditOpp ? '#c0392b' : '#fff', color: showEditOpp ? '#fff' : '#c0392b', cursor: 'pointer', minHeight: 36,
                  }}>{showEditOpp ? 'Cancel' : 'Edit'}</button>
                </div>
                {showEditOpp ? (
                  <EditOpportunityForm opp={opp} onSaved={() => { setShowEditOpp(false); load(); }} />
                ) : (
                  <>
                    <DetailRow label="Priority" value={opp.priority} />
                    <DetailRow label="Est. Value" value={money(opp.estimated_value)} />
                    <DetailRow label="Quoted" value={money(opp.quoted_value)} />
                    <DetailRow label="Probability" value={opp.probability ? `${opp.probability}%` : '—'} />
                    <DetailRow label="Services" value={(opp.services || []).join(', ')} />
                    <DetailRow label="Created" value={fmt(opp.created_at)} />
                    <DetailRow label="Last Activity" value={fmt(opp.last_activity_at)} />
                  </>
                )}
                <AISidebar oppId={id} />
                <ExpansionPanel oppId={id} />
                <ProposalTiers oppId={id} />
              </div>
            )}
          </div>
        ) : (
          <div style={{ background: '#fff', borderLeft: '1px solid #e2e8f0', padding: '20px 18px' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Customer</h3>
            <DetailRow label="Name" value={customer.name} />
            <DetailRow label="Phone" value={customer.phone} isPhone />
            <DetailRow label="Email" value={customer.email} isEmail />
            <DetailRow label="Address" value={[customer.address, customer.city, customer.state, customer.zip_code].filter(Boolean).join(', ')} />
            <DetailRow label="Company" value={customer.company} />
            <DetailRow label="Source" value={(opp.lead_source || '').replace(/_/g, ' ')} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0 12px' }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: 0 }}>Opportunity</h3>
              <button data-testid="edit-opportunity-btn" onClick={() => setShowEditOpp(!showEditOpp)} style={{
                padding: '3px 10px', fontSize: 11, fontWeight: 600, border: '1px solid #c0392b', borderRadius: 4,
                background: showEditOpp ? '#c0392b' : '#fff', color: showEditOpp ? '#fff' : '#c0392b', cursor: 'pointer',
              }}>{showEditOpp ? 'Cancel' : 'Edit'}</button>
            </div>
            {showEditOpp ? (
              <EditOpportunityForm opp={opp} onSaved={() => { setShowEditOpp(false); load(); }} />
            ) : (
              <>
                <DetailRow label="Priority" value={opp.priority} />
                <DetailRow label="Est. Value" value={money(opp.estimated_value)} />
                <DetailRow label="Quoted" value={money(opp.quoted_value)} />
                <DetailRow label="Probability" value={opp.probability ? `${opp.probability}%` : '—'} />
                <DetailRow label="Services" value={(opp.services || []).join(', ')} />
                <DetailRow label="Created" value={fmt(opp.created_at)} />
                <DetailRow label="Last Activity" value={fmt(opp.last_activity_at)} />
                {opp.notes && <>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '20px 0 8px' }}>Notes</h3>
                  <p style={{ fontSize: 13, color: '#475569', whiteSpace: 'pre-wrap' }}>{opp.notes}</p>
                </>}
              </>
            )}
            <AISidebar oppId={id} />
            <ExpansionPanel oppId={id} />
            <ProposalTiers oppId={id} />
          </div>
        )}
      </div>

      {/* Modals */}
      {showActivityForm && <ActivityModal oppId={id} customerId={customer.id} onClose={() => { setShowActivityForm(false); load(); }} />}
      {showTaskForm && <TaskModal oppId={id} customerId={customer.id} onClose={() => { setShowTaskForm(false); load(); }} />}
      {showEmailComposer && <EmailComposer oppId={id} customerId={customer.id} customerEmail={customer.email} customerName={customer.name} oppTitle={opp.title} files={files} aiDraft={aiDraft} onClose={() => { setShowEmailComposer(false); setAiDraft(null); load(); }} />}
    </div>
  );
}

/* ═══════ Sub Components ═══════ */

function ActionBtn({ label, onClick, color }) {
  return <button onClick={onClick} style={{ padding: '8px 16px', background: color, color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{label}</button>;
}

function DetailRow({ label, value, isPhone, isEmail }) {
  if (!value) return null;
  return <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13, gap: 8, flexWrap: 'wrap' }}>
    <span style={{ color: '#64748b', flexShrink: 0 }}>{label}</span>
    {isPhone ? (
      <a href={`tel:${value}`} style={{ color: '#c0392b', fontWeight: 500, textAlign: 'right', wordBreak: 'break-all' }}>{value}</a>
    ) : isEmail ? (
      <a href={`mailto:${value}`} style={{ color: '#2563eb', fontWeight: 500, textAlign: 'right', wordBreak: 'break-all' }}>{value}</a>
    ) : (
      <span style={{ color: '#1e293b', fontWeight: 500, textAlign: 'right', wordBreak: 'break-word', minWidth: 0 }}>{value}</span>
    )}
  </div>;
}

const NTX_SERVICES = ['Pipe Fence', 'Chain Link Fence', 'Privacy Fence', 'Iron Fence', 'Gate / Automation', 'Welding / Fabrication', 'Concrete', 'Land Clearing', 'Barbed Wire Fence', 'Custom Metal Work', 'Fence Repair', 'Post Holes', 'Deck / Patio', 'Carport / Barn'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

function EditOpportunityForm({ opp, onSaved }) {
  const [form, setForm] = useState({
    title: opp.title || '',
    services: opp.services || [],
    estimated_value: opp.estimated_value || '',
    quoted_value: opp.quoted_value || '',
    probability: opp.probability || '',
    priority: opp.priority || 'medium',
    notes: opp.notes || '',
    lead_source: opp.lead_source || '',
  });
  const [saving, setSaving] = useState(false);
  const [reanalyzed, setReanalyzed] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleService = (svc) => {
    setForm(f => ({
      ...f,
      services: f.services.includes(svc) ? f.services.filter(s => s !== svc) : [...f.services, svc],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        title: form.title,
        services: form.services,
        estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null,
        quoted_value: form.quoted_value ? parseFloat(form.quoted_value) : null,
        probability: form.probability ? parseInt(form.probability) : null,
        priority: form.priority,
        notes: form.notes || null,
        lead_source: form.lead_source || null,
      };
      const res = await fetch(`${API}/api/sales/opportunities/${opp.id}`, authOpts({
        method: 'PUT', body: JSON.stringify(body),
      }));
      if (res.ok) {
        setReanalyzed(true);
        setTimeout(() => onSaved(), 1200);
      }
    } finally { setSaving(false); }
  };

  const fieldStyle = { width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, boxSizing: 'border-box', background: '#fff' };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3, display: 'block' };

  return (
    <div data-testid="edit-opportunity-form" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div>
        <label style={labelStyle}>Title</label>
        <input data-testid="edit-opp-title" value={form.title} onChange={e => set('title', e.target.value)} style={fieldStyle} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <div>
          <label style={labelStyle}>Est. Value ($)</label>
          <input data-testid="edit-opp-estimated-value" type="number" value={form.estimated_value} onChange={e => set('estimated_value', e.target.value)} style={fieldStyle} />
        </div>
        <div>
          <label style={labelStyle}>Quoted ($)</label>
          <input data-testid="edit-opp-quoted-value" type="number" value={form.quoted_value} onChange={e => set('quoted_value', e.target.value)} style={fieldStyle} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <div>
          <label style={labelStyle}>Probability (%)</label>
          <input data-testid="edit-opp-probability" type="number" min="0" max="100" value={form.probability} onChange={e => set('probability', e.target.value)} style={fieldStyle} />
        </div>
        <div>
          <label style={labelStyle}>Priority</label>
          <select data-testid="edit-opp-priority" value={form.priority} onChange={e => set('priority', e.target.value)} style={fieldStyle}>
            {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label style={labelStyle}>Services</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {NTX_SERVICES.map(svc => (
            <button key={svc} data-testid={`edit-svc-${svc.replace(/[^a-zA-Z]/g, '')}`}
              onClick={() => toggleService(svc)}
              style={{
                padding: '3px 8px', fontSize: 10, borderRadius: 4, cursor: 'pointer', border: '1px solid',
                background: form.services.includes(svc) ? '#c0392b' : '#fff',
                color: form.services.includes(svc) ? '#fff' : '#64748b',
                borderColor: form.services.includes(svc) ? '#c0392b' : '#d1d5db',
              }}>{svc}</button>
          ))}
        </div>
      </div>
      <div>
        <label style={labelStyle}>Notes</label>
        <textarea data-testid="edit-opp-notes" value={form.notes} onChange={e => set('notes', e.target.value)} rows={3}
          style={{ ...fieldStyle, resize: 'vertical' }} />
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button data-testid="save-opportunity-btn" onClick={save} disabled={saving || !form.title.trim()}
          style={{ padding: '7px 16px', background: reanalyzed ? '#16a34a' : '#c0392b', color: '#fff', border: 'none', borderRadius: 5,
            fontSize: 12, fontWeight: 600, cursor: saving ? 'default' : 'pointer', flex: 1 }}>
          {reanalyzed ? 'Saved — Expansion Re-Analyzing...' : saving ? 'Saving...' : 'Save & Auto Re-Analyze'}
        </button>
      </div>
      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: -4 }}>
        Changing services, value, or notes auto-triggers expansion re-analysis
      </div>
    </div>
  );
}

function TimelineTab({ activities }) {
  return (
    <div data-testid="timeline-tab">
      {activities.length === 0 && <p style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>No activity yet</p>}
      {activities.map(a => (
        <div key={a.id} style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 14 }}>{a.type === 'call' ? '📞' : a.type === 'email' ? '📧' : a.type === 'note' ? '📝' : a.type === 'stage_change' ? '🔄' : a.type === 'system' ? '⚙️' : a.type === 'site_visit' ? '📍' : '📋'}</span>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: '#1e293b' }}>{a.description}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{fmt(a.created_at)} — {a.created_by}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TasksTab({ tasks, onRefresh }) {
  const now = new Date().toISOString();
  const completeTask = async (taskId) => {
    await fetch(`${API}/api/sales/tasks/${taskId}/complete`, { method: 'PUT', body: JSON.stringify({ result: 'Completed', next_action: '' }) });
    onRefresh();
  };
  return (
    <div data-testid="tasks-tab">
      {tasks.length === 0 && <p style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>No tasks</p>}
      {tasks.map(t => {
        const overdue = t.status === 'pending' && t.due_at && t.due_at < now;
        return (
          <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f1f5f9', background: overdue ? '#fef2f2' : 'transparent', borderRadius: 6, paddingLeft: 8, paddingRight: 8 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#1e293b' }}>{t.description}</div>
              <div style={{ fontSize: 11, color: overdue ? '#dc2626' : '#94a3b8' }}>{t.task_type} — Due: {t.due_at ? fmtDate(t.due_at) : 'No date'}{overdue ? ' — OVERDUE' : ''}</div>
            </div>
            {t.status === 'pending' && <button onClick={() => completeTask(t.id)} style={{ padding: '6px 14px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', minHeight: 36, flexShrink: 0 }}>Done</button>}
            {t.status === 'completed' && <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>Completed</span>}
          </div>
        );
      })}
    </div>
  );
}

function FilesTab({ files, customerId, oppId, onRefresh }) {
  const fileInput = useRef();
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState('other');

  const handleUpload = async (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles.length) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('customer_id', customerId);
    formData.append('opportunity_id', oppId);
    formData.append('category', category);
    for (let f of selectedFiles) formData.append('files', f);
    await fetch(`${API}/api/files/upload-multiple`, {
      method: 'POST', credentials: 'include', body: formData,
    });
    setUploading(false);
    fileInput.current.value = '';
    onRefresh();
  };

  return (
    <div data-testid="files-tab">
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={category} onChange={e => setCategory(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, minHeight: 36 }}>
          {['customer_photos','site_photos','plans','drawings','estimates','proposals','contracts','hoa','engineering','material_specs','financing','internal_only','other'].map(c => (
            <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <button onClick={() => fileInput.current?.click()} disabled={uploading}
          style={{ padding: '8px 14px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 40 }}>
          {uploading ? 'Uploading...' : 'Upload Files'}
        </button>
        <input ref={fileInput} type="file" multiple onChange={handleUpload} style={{ display: 'none' }} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" />
      </div>
      {files.length === 0 && <p style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>No files uploaded</p>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
        {files.map(f => (
          <div key={f.id} style={{ background: '#f8fafc', borderRadius: 8, padding: 12, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.original_filename}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{(f.file_size / 1024).toFixed(0)} KB — {f.category?.replace(/_/g, ' ')}</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDate(f.upload_date)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmailsTab({ emails }) {
  return (
    <div data-testid="emails-tab">
      {emails.length === 0 && <p style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>No emails sent yet</p>}
      {emails.map(e => (
        <div key={e.id} style={{ padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{e.subject}</div>
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: e.status === 'sent' ? '#dcfce7' : e.status === 'test_logged' ? '#fef3c7' : '#fef2f2', color: e.status === 'sent' ? '#16a34a' : e.status === 'test_logged' ? '#d97706' : '#dc2626' }}>{e.status}</span>
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>From: {e.from_name} &lt;{e.from_email}&gt; → {e.to_email}</div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{fmt(e.created_at)} {e.attachments?.length ? `— ${e.attachments.length} attachment(s)` : ''}</div>
        </div>
      ))}
    </div>
  );
}

function EstimatesTab({ estimates }) {
  return (
    <div data-testid="estimates-tab">
      {(!estimates || estimates.length === 0) && <p style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>No linked estimates</p>}
      {(estimates || []).map(e => (
        <div key={e.id} style={{ background: '#f8fafc', borderRadius: 8, padding: 14, border: '1px solid #e2e8f0', marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{money(e.customer_range?.low)} – {money(e.customer_range?.high)}</div>
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: e.lead_classification === 'HOT' ? '#fef2f2' : '#f8fafc', color: e.lead_classification === 'HOT' ? '#dc2626' : '#64748b', fontWeight: 600 }}>{e.lead_classification}</span>
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Services: {(e.services || []).join(', ')}</div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{fmt(e.created_at)} — Score: {e.lead_score}</div>
        </div>
      ))}
    </div>
  );
}

/* ═══════ MODALS ═══════ */

function ConfirmModal({ title, onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 360, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>{title}</h3>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13, minHeight: 40 }}>Cancel</button>
          <button onClick={onConfirm} style={{ padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, minHeight: 40 }}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

function LostReasonModal({ type, onSubmit, onCancel }) {
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>Mark as {type === 'lost' ? 'Lost' : 'Disqualified'}</h3>
        <select value={reason} onChange={e => setReason(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginBottom: 12, boxSizing: 'border-box' }}>
          <option value="">Select reason...</option>
          {LOST_REASONS.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
        </select>
        <textarea value={detail} onChange={e => setDetail(e.target.value)} placeholder="Additional details..." rows={3}
          style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button onClick={onCancel} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13, minHeight: 40 }}>Cancel</button>
          <button onClick={() => onSubmit(reason, detail)} disabled={!reason}
            style={{ padding: '8px 16px', background: reason ? '#dc2626' : '#ccc', color: '#fff', border: 'none', borderRadius: 6, cursor: reason ? 'pointer' : 'default', fontSize: 13, fontWeight: 600, minHeight: 40 }}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function ActivityModal({ oppId, customerId, onClose }) {
  const [type, setType] = useState('call');
  const [desc, setDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({});

  const save = async () => {
    if (!desc.trim()) return;
    setSaving(true);
    await fetch(`${API}/api/sales/activities`, authOpts({
      method: 'POST', body: JSON.stringify({ opportunity_id: oppId, customer_id: customerId, type, description: desc, metadata: meta }),
    }));
    setSaving(false);
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>Log Activity</h3>
        <select value={type} onChange={e => setType(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginBottom: 12, boxSizing: 'border-box' }}>
          {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Describe what happened..." rows={4}
          style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box', marginBottom: 10 }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569', marginBottom: 8 }}>
          <input type="checkbox" onChange={e => setMeta(prev => ({ ...prev, customer_responded: e.target.checked }))} /> Customer responded
        </label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13, minHeight: 40 }}>Cancel</button>
          <button onClick={save} disabled={saving || !desc.trim()} style={{ padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, minHeight: 40 }}>
            {saving ? 'Saving...' : 'Log Activity'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TaskModal({ oppId, customerId, onClose }) {
  const [type, setType] = useState('follow_up');
  const [desc, setDesc] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('medium');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!desc.trim()) return;
    setSaving(true);
    await fetch(`${API}/api/sales/tasks`, authOpts({
      method: 'POST',
      body: JSON.stringify({ opportunity_id: oppId, customer_id: customerId, task_type: type, description: desc, due_at: dueDate ? new Date(dueDate).toISOString() : null, priority }),
    }));
    setSaving(false);
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>Create Task</h3>
        <select value={type} onChange={e => setType(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginBottom: 10, boxSizing: 'border-box' }}>
          {['follow_up','call','email','text','site_visit','send_estimate','send_financing','meeting','other'].map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Task description..." rows={3}
          style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box', marginBottom: 10 }} />
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={{ flex: 1, minWidth: 140, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
          <select value={priority} onChange={e => setPriority(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
            {['low','medium','high','urgent'].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13, minHeight: 40 }}>Cancel</button>
          <button onClick={save} disabled={saving || !desc.trim()} style={{ padding: '8px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, minHeight: 40 }}>
            {saving ? 'Saving...' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmailComposer({ oppId, customerId, customerEmail, customerName, oppTitle, files, aiDraft, onClose }) {
  const [to, setTo] = useState(customerEmail || '');
  const [subject, setSubject] = useState(aiDraft?.subject || `Re: ${oppTitle}`);
  const [body, setBody] = useState(aiDraft?.body || '');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [showAIWriter, setShowAIWriter] = useState(!aiDraft);

  useEffect(() => {
    fetch(`${API}/api/sales/email/templates`, authOpts())
      .then(r => r.ok ? r.json() : []).then(setTemplates);
  }, []);

  const handleAIDraft = (draft) => {
    setSubject(draft.subject);
    setBody(draft.body);
    setShowAIWriter(false);
  };

  const applyTemplate = (tplId) => {
    const tpl = templates.find(t => t.id === tplId);
    if (!tpl) return;
    setSubject(tpl.subject.replace(/\{\{customer_name\}\}/g, customerName || '').replace(/\{\{project_type\}\}/g, oppTitle || ''));
    setBody(tpl.body_html.replace(/\{\{customer_name\}\}/g, customerName || '').replace(/\{\{project_type\}\}/g, oppTitle || ''));
  };

  const send = async () => {
    if (!to || !subject) return;
    setSending(true);
    const res = await fetch(`${API}/api/sales/email/send`, authOpts({
      method: 'POST',
      body: JSON.stringify({
        to_email: to, subject, body_html: body.replace(/\n/g, '<br/>'),
        customer_id: customerId, opportunity_id: oppId,
        attachment_file_ids: selectedFiles.filter(f => !f.isGallery).map(f => f.id),
        gallery_file_ids: selectedFiles.filter(f => f.isGallery).map(f => f.id),
        api_base: API,
      }),
    }));
    const data = await res.json();
    setSending(false);
    setResult(data);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12 }}>
      <div data-testid="email-composer" style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Send Email</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>x</button>
        </div>

        {result ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>{result.status === 'sent' || result.status === 'test_logged' ? '✓' : '!'}</div>
            <p style={{ fontSize: 15, fontWeight: 600, color: result.status === 'failed' ? '#dc2626' : '#16a34a' }}>
              {result.status === 'sent' ? 'Email Sent!' : result.status === 'test_logged' ? 'Email Logged (Test Mode)' : 'Send Failed'}
            </p>
            {result.error && <p style={{ fontSize: 13, color: '#dc2626' }}>{result.error}</p>}
            {result.status === 'test_logged' && <p style={{ fontSize: 12, color: '#f59e0b' }}>Resend is not configured yet. Email was logged to the timeline.</p>}
            <button onClick={onClose} style={{ marginTop: 16, padding: '8px 20px', background: '#1e293b', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Close</button>
          </div>
        ) : (
          <div style={{ padding: '16px 20px' }}>
            {/* AI Email Writer */}
            <div style={{ marginBottom: 12 }}>
              <button data-testid="toggle-ai-writer" onClick={() => setShowAIWriter(!showAIWriter)}
                style={{
                  padding: '7px 14px', background: showAIWriter ? '#4338ca' : '#eef2ff', color: showAIWriter ? '#fff' : '#4338ca',
                  border: '1px solid #c7d2fe', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                <span style={{ fontSize: 14 }}>✨</span> AI Draft
              </button>
            </div>
            {showAIWriter && <AIEmailWriter oppId={oppId} onDraftReady={handleAIDraft} />}

            {templates.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Template</label>
                <select onChange={e => applyTemplate(e.target.value)} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginTop: 4 }}>
                  <option value="">None — Write from scratch</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>To</label>
              <input value={to} onChange={e => setTo(e.target.value)} placeholder="customer@email.com"
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginTop: 4, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Subject</label>
              <input value={subject} onChange={e => setSubject(e.target.value)}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginTop: 4, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Message</label>
              <textarea value={body} onChange={e => setBody(e.target.value)} rows={8}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginTop: 4, resize: 'vertical', boxSizing: 'border-box' }} />
            </div>

            {/* File Attachments */}
            {files.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Attach Files</label>
                <div style={{ maxHeight: 120, overflowY: 'auto', marginTop: 4, border: '1px solid #e2e8f0', borderRadius: 6, padding: 8 }}>
                  {files.filter(f => f.category !== 'internal_only').map(f => (
                    <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 12, color: '#475569', cursor: 'pointer' }}>
                      <input type="checkbox" checked={selectedFiles.some(sf => sf.id === f.id)}
                        onChange={e => {
                          if (e.target.checked) setSelectedFiles(prev => [...prev, { id: f.id, isGallery: false }]);
                          else setSelectedFiles(prev => prev.filter(sf => sf.id !== f.id));
                        }} />
                      {f.original_filename} ({(f.file_size / 1024).toFixed(0)} KB)
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
              <button onClick={onClose} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button data-testid="send-email-btn" onClick={send} disabled={sending || !to}
                style={{ padding: '8px 20px', background: sending ? '#999' : '#c0392b', color: '#fff', border: 'none', borderRadius: 6, cursor: sending ? 'wait' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                {sending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function EstimateAttacher({ oppId, customerId, onDone, extractions }) {
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/sales/opportunity/${oppId}/extract-estimate`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) { const d = await res.json(); alert(d.detail || 'Upload failed'); return; }
      onDone();
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="estimate-attacher" style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: 16, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1e293b' }}>Estimate Documents</h4>
        <label data-testid="attach-estimate-btn" style={{ padding: '6px 14px', background: uploading ? '#94a3b8' : '#1e293b', color: '#fff',
          borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: uploading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          {uploading ? (
            <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 11-6.22-8.56"/></svg> Analyzing...</>
          ) : (
            <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> Attach & Analyze</>
          )}
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,image/*,application/pdf"
            onChange={handleUpload} style={{ display: 'none' }} disabled={uploading} />
        </label>
      </div>

      {extractions.length === 0 && !uploading && (
        <p style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', padding: 10 }}>No estimate documents attached. Upload a PDF or photo of an existing estimate for AI analysis.</p>
      )}

      {extractions.map((ex) => (
        <div key={ex.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
          <button onClick={() => setExpanded(expanded === ex.id ? null : ex.id)}
            style={{ width: '100%', padding: '10px 12px', background: '#f8fafc', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
            <div>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{ex.filename}</span>
              <span style={{ fontSize: 11, color: ex.status === 'completed' ? '#16a34a' : '#dc2626', marginLeft: 8, fontWeight: 600 }}>
                {ex.status === 'completed' ? 'Extracted' : 'Failed'}
              </span>
            </div>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDate(ex.created_at)}</span>
          </button>

          {expanded === ex.id && ex.extracted_data && (
            <div style={{ padding: 12, borderTop: '1px solid #e2e8f0', fontSize: 12 }}>
              {ex.extracted_data.customer_name && <div style={{ marginBottom: 6 }}><strong>Customer:</strong> {ex.extracted_data.customer_name}</div>}
              {ex.extracted_data.company_name && <div style={{ marginBottom: 6 }}><strong>From:</strong> {ex.extracted_data.company_name}</div>}
              {ex.extracted_data.competitor_name && <div style={{ marginBottom: 6 }}><strong>Competitor:</strong> <span style={{ color: '#dc2626', fontWeight: 600 }}>{ex.extracted_data.competitor_name}</span></div>}
              {ex.extracted_data.project_description && <div style={{ marginBottom: 6 }}><strong>Scope:</strong> {ex.extracted_data.project_description}</div>}
              {ex.extracted_data.dimensions && <div style={{ marginBottom: 6 }}><strong>Dimensions:</strong> {ex.extracted_data.dimensions}</div>}
              {ex.extracted_data.total != null && <div style={{ marginBottom: 6 }}><strong>Total:</strong> <span style={{ color: '#16a34a', fontWeight: 700 }}>${Number(ex.extracted_data.total).toLocaleString()}</span></div>}
              {ex.extracted_data.deposit_required != null && <div style={{ marginBottom: 6 }}><strong>Deposit:</strong> ${Number(ex.extracted_data.deposit_required).toLocaleString()}</div>}

              {ex.extracted_data.line_items?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <strong>Line Items:</strong>
                  <div style={{ overflowX: 'auto', marginTop: 4 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead><tr style={{ background: '#f1f5f9' }}>
                        <th style={{ padding: '4px 6px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Description</th>
                        <th style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Qty</th>
                        <th style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Unit Price</th>
                        <th style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Total</th>
                      </tr></thead>
                      <tbody>{ex.extracted_data.line_items.map((li, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '4px 6px' }}>{li.description}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'right' }}>{li.quantity ?? '-'}{li.unit ? ` ${li.unit}` : ''}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'right' }}>{li.unit_price != null ? `$${Number(li.unit_price).toLocaleString()}` : '-'}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>{li.total != null ? `$${Number(li.total).toLocaleString()}` : '-'}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </div>
              )}

              {ex.extracted_data.materials_mentioned?.length > 0 && (
                <div style={{ marginTop: 8 }}><strong>Materials:</strong> {ex.extracted_data.materials_mentioned.join(', ')}</div>
              )}
              {ex.extracted_data.notes && <div style={{ marginTop: 8 }}><strong>Notes:</strong> {ex.extracted_data.notes}</div>}
              <div style={{ marginTop: 8, color: '#94a3b8', fontSize: 11 }}>Confidence: {ex.extracted_data.confidence || 'N/A'}</div>
            </div>
          )}
        </div>
      ))}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
